import type { SVGProps } from "react";

interface SVGRProps {
  title?: string;
  titleId?: string;
  desc?: string;
  descId?: string;
  width?: number;
  variant?: "light" | "dark" | "auto";
}

export default function Logo({
  title = "Earlyup Logo",
  titleId,
  desc = "Earlyup - Connecting People, Empowering Lives",
  descId,
  variant = "auto",
  ...props
}: SVGProps<SVGSVGElement> & SVGRProps) {
  // Theme-aware color configuration
  const getColors = () => {
    switch (variant) {
      case "light":
        return {
          mainFill: "#000000",
          useGradients: true,
          gradientStart: "#06c9ff",
          gradientEnd: "#ad44ff",
        };
      case "dark":
        return {
          mainFill: "#ffffff",
          useGradients: true,
          gradientStart: "#06c9ff",
          gradientEnd: "#ad44ff",
        };
      case "auto":
      default:
        return {
          // Not hsl(var(--foreground)): the token is already a full hsl()
          // colour, so wrapping it built hsl(hsl(...)) — an invalid fill that
          // paints black. In dark that was a black letter on a black page.
          mainFill: "var(--foreground)",
          useGradients: true,
          gradientStart: "#06c9ff",
          gradientEnd: "#ad44ff",
        };
    }
  };

  const colors = getColors();

  return (
    <svg
      id="ergon-logo"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 485.69 487.17"
      shapeRendering="geometricPrecision"
      textRendering="optimizeLegibility"
      aria-labelledby={titleId}
      aria-describedby={descId}
      {...props}
    >
      {desc ? <desc id={descId}>{desc}</desc> : null}
      {title ? <title id={titleId}>{title}</title> : null}

      <defs>
        {colors.useGradients && (
          <>
            <linearGradient
              id="ergon-gradient-1"
              x1="-101.1"
              y1="821.05"
              x2="-410.02"
              y2="458.93"
              gradientTransform="matrix(1 0 0 -1 444 843.17)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={colors.gradientStart} />
              <stop offset="65%" stopColor={colors.gradientEnd} />
            </linearGradient>

            <linearGradient
              id="ergon-gradient-2"
              x1="-63.01"
              y1="788.56"
              x2="-371.92"
              y2="426.43"
              gradientTransform="matrix(1 0 0 -1 444 843.17)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={colors.gradientStart} />
              <stop offset="65%" stopColor={colors.gradientEnd} />
            </linearGradient>

            <linearGradient
              id="ergon-gradient-3"
              x1="76.94"
              y1="669.17"
              x2="-231.97"
              y2="307.05"
              gradientTransform="matrix(1 0 0 -1 444 843.17)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={colors.gradientStart} />
              <stop offset="65%" stopColor={colors.gradientEnd} />
            </linearGradient>
          </>
        )}
      </defs>

      <g id="ergon-logo-group">
        {/* Main "e" letter */}
        <path
          d="M170.1 215.63c7.3-31.57 28.64-55.84 65.56-55.84 34.5 0 55.38 24.27 60.72 55.84H170.1zm65.56 99.55c-34.5 0-60.21-23.81-65.56-58.26h184.54l.98-6.79c.6-4.37 1.44-10.18 1.44-17.48 0-70.44-53.42-123.86-121.4-123.86-72.86 0-126.28 53.42-126.28 128.7s53.42 128.7 126.28 128.7h66.44v-51h-66.44z"
          fillRule="evenodd"
          fill={colors.mainFill}
        />

        {/* Left circular element */}
        <path
          d="M97.55 119.72c23.67 0 42.86-19.19 42.86-42.86S121.22 34 97.55 34 54.69 53.19 54.69 76.86c0 2.47.21 4.88.61 7.24C20.79 125.25 0 178.43 0 236.49c0 123.5 94.08 224.89 214.05 235.56a61.807 61.807 0 01-6.54-27.75c0-9.19 2-17.92 5.59-25.77-90.31-10.89-160.31-88.24-160.31-182.05 0-44.41 15.69-85.14 41.8-116.87.98.07 1.96.1 2.96.1z"
          fill={
            colors.useGradients ? "url(#ergon-gradient-1)" : colors.mainFill
          }
        />

        {/* Top circular element */}
        <path
          d="M442.82 280.24c23.67 0 42.86-19.19 42.86-42.86 0-14.44-7.14-27.22-18.09-34.98C451.17 87.94 353.3 0 235.01 0c-36.19 0-70.47 8.23-101.09 22.94 15.09 10.2 25.67 26.57 28.12 45.48 22.34-9.84 47.02-15.29 72.97-15.29 90.01 0 164.78 65.68 179.56 152.01-8.96 7.86-14.61 19.39-14.61 32.24 0 23.67 19.19 42.86 42.86 42.86z"
          fill={
            colors.useGradients ? "url(#ergon-gradient-2)" : colors.mainFill
          }
        />

        {/* Bottom circular element */}
        <path
          d="M226.73 444.3c0 23.67 19.19 42.86 42.86 42.86 17.75 0 32.67-10.7 39.19-26.08 75.17-24.99 133.54-87.12 153.65-164.8-6.17 2.05-12.76 3.17-19.61 3.17-12.29 0-23.75-3.58-33.39-9.74-16.88 56.1-59.76 100.82-114.52 119.99-7.09-5.2-15.84-8.27-25.31-8.27-23.67 0-42.86 19.19-42.86 42.86z"
          fill={
            colors.useGradients ? "url(#ergon-gradient-3)" : colors.mainFill
          }
        />
      </g>
    </svg>
  );
}
